import React, {
  CSSProperties,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Amplify, Auth, Hub } from 'aws-amplify';
import { Authenticator, View, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import {
  AWS_REGION,
  AWS_USER_POOL_ID,
  AWS_USER_POOL_WEB_CLIENT_ID,
  AWS_IDENTITY_POOL_ID,
} from './externalConfig';
import PtMetricsLogoIcon from './assets/ptMetricsLogo.svg';
import { fonts } from './style/fonts';
import { signInCss } from './style/signIn'; // Import your styles
import { Global } from '@emotion/react';
import RefreshSessionModal from './components/app/RefreshSessionModal';
import { LogoutReason, recordLogin } from './services/authEventService';
import PrivacyPolicyPage from './components/privacy-policy/PrivacyPolicyPage';
import { colors } from './style/colors';
import { HubCallback, HubCapsule } from '@aws-amplify/core';
import { validateEmail } from './components/forms/create-user-form/userFormValidation';
import App from './App';
import useSignOut from './hooks/useSignOut';
import LivePasswordRules from './components/auth/LivePasswordRules';
import ConfirmResetPasswordFooter from './components/auth/ConfirmResetPasswordFooter';
import {
  getAuthRegionConfig,
  clearAuthConfigCache,
  isAuthRouterEnabled,
  AuthRegionConfig,
} from './services/authRouterService';
import {
  setRegionalApiBaseUrl,
  clearRegionalApiBaseUrl,
  getStoredAuthConfig,
  setStoredAuthConfig,
} from './services/regionalApiStorage';
import { getOrCreateDeviceId } from './services/deviceId';
import { clearPortalStorageOnSignOut } from './services/signOutStorage';
import MfaInlinePanel, {
  MfaPanelStatus,
} from './components/auth/MfaInlinePanel';
import {
  initRum,
  reinitRumForRegion,
  trackPortalEvent,
} from './observability/rum/rumClient';
import { PORTAL_EVENTS } from './observability/rum/rumEvents';
import { classifyForgotPasswordError } from './services/forgotPasswordErrors';
import { installFirstLoginPasswordRecovery } from './services/firstLoginPassword';

// Public /terms page: only fetched when that URL is opened.
const TermsOfUsePage = lazy(
  () =>
    import(
      /* webpackChunkName: "patient-terms" */ './components/terms-of-use/TermsOfUsePage'
    ),
);

// Returns `value` only if it is a non-empty string, else `fallback`. Guards
// against a runtime auth source (stored config / Auth Router) supplying an
// empty/blank pool id or client id — configuring Amplify with an empty auth
// property throws "missing required auth properties" and white-screens the app.
// `??` alone is not enough: it only falls back on null/undefined, not "".
function nonEmpty(value: string | undefined, fallback: string): string {
  return value && value.trim() !== '' ? value : fallback;
}

// On page refresh, restore the regional Amplify config if the user was previously
// routed to a non-default region by the Auth Router. Without this, Amplify would
// initialize with the build-time default Cognito pool and fail to find the session
// tokens (which are keyed by the regional client ID in sessionStorage).
const storedAuthConfig = getStoredAuthConfig();
Amplify.configure({
  Auth: {
    region: nonEmpty(storedAuthConfig?.region, AWS_REGION),
    userPoolId: nonEmpty(storedAuthConfig?.cognitoPoolId, AWS_USER_POOL_ID),
    userPoolWebClientId: nonEmpty(
      storedAuthConfig?.clientId,
      AWS_USER_POOL_WEB_CLIENT_ID,
    ),
    // Identity Pool: required so Auth.currentCredentials() can vend the AWS
    // credentials CloudWatch RUM uses to send events — guest creds before login
    // (so pre-login/auth errors are captured) and authenticated creds after.
    // Restore the REGIONAL identity pool if a prior login stored one: on refresh
    // the user pool is restored to the regional pool, so the identity pool must
    // match (the eu default pool won't federate a regional user-pool token).
    identityPoolId: nonEmpty(
      storedAuthConfig?.identityPoolId,
      AWS_IDENTITY_POOL_ID,
    ),
    storage: sessionStorage,
    // NOTE: do NOT set authenticationFlowType: 'CUSTOM_AUTH' globally here. The
    // Amplify UI <Authenticator> only supports the SRP/PASSWORD flows for its own
    // sign-in machine and renders a BLANK screen under a global CUSTOM_AUTH flow.
    // When clinician email-MFA is enabled (after Mercury validation), set the
    // CUSTOM_AUTH flow type just-in-time inside handleSignIn (immediately before
    // Auth.signIn) and restore the default afterwards, so the Authenticator keeps
    // loading under SRP. MFA currently ships inert (CLINICIAN_MFA_MODE=off), so
    // the SRP default is correct and the clinician custom-challenge never runs.
  },
});
if (storedAuthConfig) {
  setRegionalApiBaseUrl(storedAuthConfig.apiBaseUrl);
}

// Initialize CloudWatch RUM at bootstrap — BEFORE the auth gate — so login /
// pre-auth errors (our primary debugging surface) are captured. The RUM
// credential provider calls Auth.currentCredentials(), which returns GUEST
// credentials pre-login (guest-role ingestion) and authenticated credentials
// after sign-in, so a single session spans both. Must run after the
// Amplify.configure above (the provider needs Amplify configured). initRum is
// idempotent and a no-op when RUM is disabled (empty RUM_APP_MONITOR_ID).
initRum();

// If a prior login routed this user to a non-default region, re-point RUM to
// that region's monitor now (page-refresh restore). A fresh login drives the
// same switch via reconfigureAmplifyForRegion below. No-op for the default
// region or a single-region (mercury) build.
if (storedAuthConfig?.region) {
  reinitRumForRegion(storedAuthConfig.region);
}

// First-login password set: recover when its response is lost in transit
// (see services/firstLoginPassword.ts). The probe sign-in runs under the SRP
// default, like the re-initiate in handleSignIn below.
installFirstLoginPasswordRecovery(
  Auth,
  (username, password) =>
    Auth.signIn(username, password, { deviceId: getOrCreateDeviceId() }),
  (outcome, code) =>
    trackPortalEvent(
      outcome === 'recovered'
        ? PORTAL_EVENTS.AUTH_PASSWORD_CHANGE_SUCCEEDED
        : PORTAL_EVENTS.AUTH_PASSWORD_CHANGE_FAILED,
      { outcome, featureArea: 'first_login', normalizedErrorCode: code },
    ),
);

/**
 * Reconfigures Amplify Auth with the regional Cognito configuration.
 * Called before sign-in when Auth Router returns a different region.
 */
function reconfigureAmplifyForRegion(config: AuthRegionConfig): void {
  // Fall back to the build-time defaults for any field the Auth Router returns
  // empty. The Auth Router fails open to a default config sourced from its env
  // vars; if those are unset it can return a 200 with empty pool/client ids,
  // and configuring Amplify with blanks throws "missing required auth
  // properties". Defaulting here keeps sign-in working against the home region.
  Amplify.configure({
    Auth: {
      region: nonEmpty(config.region, AWS_REGION),
      userPoolId: nonEmpty(config.cognitoPoolId, AWS_USER_POOL_ID),
      userPoolWebClientId: nonEmpty(
        config.clientId,
        AWS_USER_POOL_WEB_CLIENT_ID,
      ),
      // Per-region identity pool when the Auth Router supplies one, else the
      // build-time default (see the identityPoolId note in the build-time
      // config above).
      identityPoolId: nonEmpty(config.identityPoolId, AWS_IDENTITY_POOL_ID),
      storage: sessionStorage,
      // (See the build-time config note above: authenticationFlowType is left at
      // the SRP default so the Amplify UI Authenticator renders; set CUSTOM_AUTH
      // just-in-time in handleSignIn when MFA is enabled, not globally here.)
    },
  });

  // Store the regional API base URL for use by httpClient
  setRegionalApiBaseUrl(config.apiBaseUrl);

  // Persist the auth config so it survives page refresh. Use the same
  // non-empty fallbacks as above so an empty Auth Router response can't poison
  // sessionStorage and break the next page-load Amplify.configure.
  setStoredAuthConfig({
    region: nonEmpty(config.region, AWS_REGION),
    cognitoPoolId: nonEmpty(config.cognitoPoolId, AWS_USER_POOL_ID),
    clientId: nonEmpty(config.clientId, AWS_USER_POOL_WEB_CLIENT_ID),
    apiBaseUrl: config.apiBaseUrl,
    // Persist the regional identity pool so a refresh restores it (see the
    // bootstrap identityPoolId note above).
    identityPoolId: nonEmpty(config.identityPoolId, AWS_IDENTITY_POOL_ID),
  });

  // Re-point RUM to this region's own monitor: the identity pool just switched
  // to config.region, so post-login RUM uploads are signed with that region's
  // role — authorized only on that region's monitor. No-op for the default
  // region. Pre-login/login events already landed on the default monitor.
  reinitRumForRegion(nonEmpty(config.region, AWS_REGION));
}

const components = {
  Header(username: string) {
    const currentState = useAuthenticator();
    let headerText;
    let headline;

    if (currentState.route == 'confirmResetPassword') {
      headerText = 'Reset Password';
      headline = `Check your email for a code sent to ${username}, 
      Check your spam folder if you don’t see the email. 
      Enter your new password below.`;
    } else if (currentState.route == 'resetPassword') {
      headerText = 'Forgot Password';
      headline =
        'Please enter your email address to receive a message with a code to reset your password.';
    } else if (currentState.route == 'signIn') {
      headerText = 'Sign In';
      headline = null;
    } else if (currentState.route == 'forceNewPassword') {
      headerText = 'Reset Password';
      headline = 'Enter your new password below.';
    }
    return (
      <View
        style={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          alignSelf: 'center',
          margin: 'auto',
          flexDirection: 'column',
          paddingTop: '60px',
          width: '100%',
        }}
        textAlign="center"
      >
        <PtMetricsLogoIcon width="371" height="70" />
        <div style={fonts.modalHeader as CSSProperties}>{headerText}</div>
        {headline && (
          <div
            style={{
              ...(fonts.text as CSSProperties),
              paddingTop: '20px',
            }}
          >
            {headline}
          </div>
        )}
      </View>
    );
  },
  ForceNewPassword: {
    FormFields() {
      return (
        <>
          <Authenticator.ForceNewPassword.FormFields />
          {/* Was five hand-written lines, one of which tested `hasUpper` for
              the LOWERCASE message — so an uppercase-but-no-lowercase password
              showed no unmet rule at all while the submit button stayed
              disabled, leaving the user at a dead end. Deriving the list from
              PASSWORD_RULES makes that class of mismatch unrepresentable.
              Shared with the ConfirmResetPassword screen. Do not re-inline. */}
          <LivePasswordRules />
        </>
      );
    },
  },
};

function AppWithLogin() {
  const INACTIVITY_LIMIT = 3600000;
  const TIME_REFRESH_TOKEN_MSG = 60;
  const inactivityTimeout = useRef<number | undefined>(undefined);
  const [showSessionTimeOutModal, setShowSessionTimeOutModal] =
    useState<boolean>(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState<string>('');
  const signOut = useSignOut();
  const authChannel = useRef<BroadcastChannel | undefined>();

  // --- Clinician email-MFA (CUSTOM_CHALLENGE) overlay state ---
  // When PASSWORD_VERIFIER succeeds and the Lambda decides MFA is required, it
  // returns a CUSTOM_CHALLENGE; we drive the 6-digit code entry via the inline
  // MfaInlinePanel (which replaces the email/password card in the sign-in view,
  // not a popup) and resolve handleSignIn only once fully authenticated. With
  // CLINICIAN_MFA_MODE=off the Lambda issues tokens straight after
  // PASSWORD_VERIFIER, so challengeName is never CUSTOM_CHALLENGE and none of
  // this runs (ships inert).
  const [mfaOpen, setMfaOpen] = useState<boolean>(false);
  const [mfaEmail, setMfaEmail] = useState<string>('');
  const [mfaStatus, setMfaStatus] = useState<MfaPanelStatus>('idle');
  const [mfaError, setMfaError] = useState<string | undefined>(undefined);
  // The pending CognitoUser returned by Auth.signIn, carried across the
  // CUSTOM_CHALLENGE rounds. amazon-cognito-identity-js mutates this object as
  // challenges are answered, so we keep the same reference for resend/submit.
  const mfaUserRef = useRef<any>(null);
  // Deferred handlers wired up by handleSignIn so the panel's buttons can drive
  // the in-flight sign-in promise (resolve on success, reject on cancel).
  const mfaSubmitRef = useRef<((code: string) => void) | null>(null);
  const mfaResendRef = useRef<(() => void) | null>(null);
  const mfaCancelRef = useRef<(() => void) | null>(null);

  // Stable callbacks for MfaInlinePanel — they only invoke the refs above, so
  // empty deps are correct. Stability matters: the panel's auto-submit effect
  // lists onSubmit as a dependency, and a fresh closure each render would re-run
  // that effect on every parent render (harmless given the panel's submittedRef
  // guard, but this keeps that guard the single source of truth).
  const handleMfaSubmit = useCallback(
    (code: string) => mfaSubmitRef.current?.(code),
    [],
  );
  const handleMfaResend = useCallback(() => mfaResendRef.current?.(), []);
  const handleMfaCancel = useCallback(() => mfaCancelRef.current?.(), []);

  const handleSignOut = async (reason: LogoutReason) => {
    try {
      await signOut(reason);
      setShowSessionTimeOutModal(false);
      // Amplify clears its own Cognito tokens and sessionStorage dies with the
      // tab, but everything the portal writes to localStorage survives
      // sign-out -- including per-clinician preferences (which hold patient and
      // case ids) and patient-search history. On a shared clinic workstation the
      // next user could read those, so purge them here. `percix_device_id` (MFA
      // device trust) is deliberately preserved -- see signOutStorage.ts.
      clearPortalStorageOnSignOut();
      // Clear Auth Router cache and regional URL on sign-out
      clearAuthConfigCache();
      clearRegionalApiBaseUrl();
    } catch (error) {}
  };

  const checkInactivity = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (user) {
        const currentTime = Date.now();
        const lastActivityTime = localStorage.getItem('last_activity_time');
        if (currentTime - Number(lastActivityTime) > INACTIVITY_LIMIT) {
          setShowSessionTimeOutModal(true);
          localStorage.setItem('session_timeout_modal', 'true');
        }
      }
    } catch (error) {}
  };

  const resetActivityTimer = () => {
    const oldValue = window.localStorage.getItem('last_activity_time');
    const newValue = Date.now().toString();
    localStorage.setItem('last_activity_time', newValue);
    const event = new StorageEvent('storage', {
      key: 'last_activity_time',
      oldValue,
      newValue,
    });

    window.dispatchEvent(event);
  };

  useEffect(() => {
    const broadcastChannel = new BroadcastChannel('auth-channel');
    authChannel.current = broadcastChannel;
    return () => {
      broadcastChannel.close();
    };
  }, []);

  const getSessionStorageData = () => {
    const cognitoKeys = Object.entries(sessionStorage).filter(([key]) =>
      key.startsWith('Cognito'),
    );

    return cognitoKeys.length ? cognitoKeys : undefined;
  };

  useEffect(() => {
    localStorage.setItem('last_activity_time', Date.now().toString());
    const listener: HubCallback = (data: HubCapsule) => {
      const { payload } = data;
      if (payload.event === 'signOut') {
        authChannel.current?.postMessage({
          type: 'signOut',
          payload: payload.event,
        });

        if (showSessionTimeOutModal) {
          setShowSessionTimeOutModal(false);
          localStorage.removeItem('session_timeout_modal');
        }
      }
      if (payload.event === 'signIn') {
        // Hub fires `signIn` only on a real sign-in (not on a reload that restores a
        // session), so this is the one place a login is audited.
        recordLogin();
        authChannel.current?.postMessage({
          type: 'signIn',
          payload: payload.event,
        });
      }
    };

    Hub.listen('auth', listener);

    const onStorageChange = (event: StorageEvent) => {
      if (event.key === 'last_activity_time') {
        if (inactivityTimeout) {
          clearTimeout(inactivityTimeout.current);
        }
        const timeout = window.setTimeout(checkInactivity, INACTIVITY_LIMIT);
        inactivityTimeout.current = timeout;
      }
      if (event.key === 'session_timeout_modal') {
        if (event.newValue === 'true') {
          setShowSessionTimeOutModal(true);
        }
        if (!event.newValue) {
          setShowSessionTimeOutModal(false);
        }
      }
    };

    window.addEventListener('storage', onStorageChange);
    authChannel.current?.addEventListener('message', (event) => {
      if (event.data.type === 'signOut') {
        signOut();
        return;
      }
      if (event.data.type === 'syncSessionStorage') {
        const sessionStorageData = getSessionStorageData();
        if (sessionStorageData) {
          return;
        }

        const incomingSessionData = event.data.payload;
        incomingSessionData.forEach(([key, value]: [string, string]) => {
          sessionStorage.setItem(key, value);
        });
        window.location.reload();
      } else if (event.data.type === 'requestSessionStorage') {
        const sessionStorageData = getSessionStorageData();
        if (!sessionStorageData) {
          return;
        }

        authChannel.current?.postMessage({
          type: 'syncSessionStorage',
          payload: sessionStorageData,
        });
      } else {
        window.location.reload();
      }
    });

    const events = [
      'mousemove',
      'mousedown',
      'keypress',
      'scroll',
      'touchstart',
    ];
    events.forEach((event) =>
      window.addEventListener(event, resetActivityTimer),
    );

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetActivityTimer),
      );
      Hub.remove('auth', listener);
      window.removeEventListener('storage', onStorageChange);
    };
  }, []);

  useEffect(() => {
    const sessionData = getSessionStorageData();
    if (!sessionData) {
      authChannel.current?.postMessage({ type: 'requestSessionStorage' });
    }
  }, []);

  const handleForgotPassword = async (rawUsername: string) => {
    // Normalize to lowercase. The Cognito pool is case-sensitive on the email
    // username (an immutable pool setting), so a user who types their email in
    // any case must still resolve to their (lowercase) account — otherwise the
    // reset silently matches no user and no email is sent. All downstream uses
    // (region lookup, Auth.forgotPassword) use this normalized value.
    const username = rawUsername.trim().toLowerCase();
    if (!validateEmail(username)) {
      throw new Error('Please enter a valid email address');
    }

    // Use Auth Router to get regional Cognito configuration for password reset
    if (isAuthRouterEnabled()) {
      try {
        const regionConfig = await getAuthRegionConfig(username);
        reconfigureAmplifyForRegion(regionConfig);
      } catch {
        // If Auth Router fails, continue with default config (graceful degradation)
      }
    }

    try {
      const data = await Auth.forgotPassword(username);
      setResetPasswordEmail(username);
      return data;
    } catch (error: unknown) {
      const classification = classifyForgotPasswordError(error);
      if (!classification.suppress) {
        throw error;
      }

      // Suppressed: the screen must look exactly like the success case, or the
      // difference itself reveals whether the account exists. That includes setting
      // `resetPasswordEmail` — the header renders it, so skipping it here (as this
      // code used to) made a real account visibly different from an unknown one.
      setResetPasswordEmail(username);

      // The user is told nothing; the operator is. Reason only — never the address.
      trackPortalEvent(PORTAL_EVENTS.AUTH_PASSWORD_RESET_UNAVAILABLE, {
        outcome: 'no_code_sent',
        normalizedErrorCode: classification.reason,
        featureArea: 'auth',
      });
    }
  };

  // Confirm step of the reset flow (code + new password). Amplify UI would
  // otherwise call Auth.forgotPasswordSubmit with the raw username the user
  // typed in the forgot-password form; normalize it to lowercase here too so
  // the confirm resolves the same (lowercase) account that handleForgotPassword
  // sent the code to. Without this override the initiate step would send the
  // code but the confirm would fail to match on a case-sensitive pool.
  const handleForgotPasswordSubmit = async ({
    username,
    code,
    password,
  }: {
    username: string;
    code: string;
    password: string;
  }) => {
    return Auth.forgotPasswordSubmit(
      username.trim().toLowerCase(),
      code,
      password,
    );
  };

  const handleSignIn = async ({
    username: rawUsername,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    // Normalize to lowercase (see handleForgotPassword). The case-sensitive pool
    // means a user must match their lowercase account regardless of typed case;
    // all downstream uses (region lookup, Auth.signIn) use this value.
    const username = rawUsername.trim().toLowerCase();
    if (!validateEmail(username)) {
      throw new Error('Please check the email format and remove any spaces.');
    }

    // Use Auth Router to get regional Cognito configuration
    // This enables multi-region authentication
    if (isAuthRouterEnabled()) {
      try {
        const regionConfig = await getAuthRegionConfig(username);
        reconfigureAmplifyForRegion(regionConfig);
      } catch {
        // If Auth Router fails, continue with default config (graceful degradation)
      }
    }

    // Persistent per-browser device id for server-side MFA device trust. Passed
    // as clientMetadata so the Lambda can decide whether a code is required.
    const deviceId = getOrCreateDeviceId();

    // Proceed with sign-in using the (potentially reconfigured) Amplify.
    // The 3rd arg (clientMetadata) reaches the Cognito triggers.
    //
    // Set the CUSTOM_AUTH flow type JUST-IN-TIME, only around this Auth.signIn
    // call, then restore the SRP default in finally. This is deliberate: setting
    // authenticationFlowType: 'CUSTOM_AUTH' globally (at module load in
    // Amplify.configure) renders a BLANK Authenticator, because the Amplify UI
    // <Authenticator>'s own sign-in machine only supports the SRP/PASSWORD flows.
    // Scoping CUSTOM_AUTH to this single call lets the clinician Lambda's
    // CUSTOM_CHALLENGE run for this sign-in while the global config stays at the
    // SRP default so the Authenticator keeps rendering.
    //
    // Re-apply the CURRENT Auth config IN FULL (region/userPoolId/clientId/
    // storage — which may have just been regionally reconfigured above) plus the
    // flow-type override, instead of passing a partial. This is robust whether
    // Auth.configure merges or replaces, so toggling the flow type can never drop
    // the regional pool routing and break multi-region sign-in.
    let user;
    const authConfig = Auth.configure() || {};
    Auth.configure({ ...authConfig, authenticationFlowType: 'CUSTOM_AUTH' });
    try {
      // channel: 'web' tells the custom-auth Lambda this is a web-portal login
      // so PATIENTS get the same password + email-MFA flow as clinicians
      // (instead of the Android app's passwordless QR path). The Android app
      // sends no channel and keeps QR. deviceId drives the MFA device-trust
      // window. Both ride clientMetadata.
      user = await Auth.signIn(username, password, {
        deviceId,
        channel: 'web',
      });
    } finally {
      // Always restore the SRP default so any subsequent Authenticator-driven
      // flow (re-render, retry, forgot/forceNewPassword) is unaffected.
      Auth.configure({
        ...authConfig,
        authenticationFlowType: 'USER_SRP_AUTH',
      });
    }

    // First-login temp-password set. The sign-in above ran under CUSTOM_AUTH, so
    // the server session would loop back into DefineAuthChallenge after the new
    // password is set and emit a CUSTOM_CHALLENGE that Amplify's
    // completeNewPassword callback can't handle ("customChallenge is not a
    // function"). Re-initiate under the restored SRP default so the Authenticator's
    // forceNewPassword flow completes and issues tokens directly. (Auth.configure
    // was already restored to USER_SRP_AUTH in the finally block above.)
    if (user?.challengeName === 'NEW_PASSWORD_REQUIRED') {
      user = await Auth.signIn(username, password, { deviceId });
      return user;
    }

    // Password-only path (patient flow, test-user/trusted-device bypass, or
    // CLINICIAN_MFA_MODE=off): no custom challenge → Authenticator proceeds as
    // today. This keeps forgotPassword / forceNewPassword / patient QR intact.
    if (user?.challengeName !== 'CUSTOM_CHALLENGE') {
      return user;
    }

    // --- MFA required: drive the 6-digit email-code challenge ---
    //
    // We await the panel resolution INSIDE handleSignIn, which leaves the
    // Amplify Authenticator's sign-in form in a "submitting" state while the
    // user reads their email. We hide the Authenticator and show the inline
    // panel in its place (so the user never sees a stuck form); Amplify v5 UI
    // tolerates the long-running submit. The panel is fully app-controlled
    // (component state), so if this ever misbehaves only the await location
    // would need to change (to a Hub/route-driven gate on mfa_verified).
    mfaUserRef.current = user;
    setMfaEmail(username);
    setMfaError(undefined);
    setMfaStatus('idle');
    setMfaOpen(true);

    return new Promise((resolve, reject) => {
      const closeModal = () => {
        setMfaOpen(false);
        setMfaStatus('idle');
        setMfaError(undefined);
        mfaUserRef.current = null;
        mfaSubmitRef.current = null;
        mfaResendRef.current = null;
        mfaCancelRef.current = null;
      };

      mfaSubmitRef.current = async (code: string) => {
        setMfaStatus('verifying');
        setMfaError(undefined);
        try {
          // sendCustomChallengeAnswer mutates and returns the same CognitoUser.
          const result = await Auth.sendCustomChallengeAnswer(
            mfaUserRef.current,
            code,
            // channel: 'web' must ride here too — the VerifyAuthChallenge event
            // carries no SRP entry, so the Lambda relies on this flag to keep a
            // patient-web login on the password+MFA path during verification.
            { deviceId, channel: 'web' },
          );
          // Cognito re-issues a CUSTOM_CHALLENGE when the code was wrong but the
          // attempt cap / lockout hasn't been hit. Stay on the panel and prompt.
          // NOTE: code-oriented copy only — never "password" wording.
          if (result?.challengeName === 'CUSTOM_CHALLENGE') {
            mfaUserRef.current = result;
            setMfaStatus('idle');
            setMfaError('Incorrect code. Please try again.');
            return;
          }
          // Success: tokens issued. Resolve handleSignIn with the authenticated
          // user so the Authenticator completes and the Hub emits signIn.
          closeModal();
          resolve(result);
        } catch (error: any) {
          // A thrown error here generally means the pending Cognito session is
          // no longer usable (too many failures / lockout / session expired).
          // Reject so the Authenticator returns to the sign-in form — but map to
          // CODE-oriented copy so Cognito's raw "Incorrect username or password"
          // (NotAuthorizedException) never surfaces as a misleading password
          // error for what is actually a wrong/expired code.
          closeModal();
          // Cognito throws NotAuthorizedException both for the attempt-cap/
          // lockout case AND for an expired pending session ("...session is
          // expired."). Distinguish them so we don't tell a user who simply
          // waited too long that they made "too many attempts". Both messages
          // are CODE-oriented — never password wording.
          const errCode = error?.code ?? error?.name;
          const rawMessage = (error?.message ?? '').toLowerCase();
          let message = 'We couldn’t verify your code. Please sign in again.';
          if (errCode === 'NotAuthorizedException') {
            message = rawMessage.includes('expired')
              ? 'Your sign-in session expired. Please sign in again.'
              : 'Too many incorrect attempts. Please sign in again.';
          }
          reject(new Error(message));
        }
      };

      mfaResendRef.current = async () => {
        setMfaStatus('resending');
        setMfaError(undefined);
        try {
          // Reuse the in-session RESEND_CODE path (sentinel answer) so the
          // Lambda re-issues a code without re-running SRP / swapping the user.
          const result = await Auth.sendCustomChallengeAnswer(
            mfaUserRef.current,
            'RESEND_CODE',
            { deviceId, action: 'RESEND_CODE', channel: 'web' },
          );
          if (result) {
            mfaUserRef.current = result;
          }
          setMfaStatus('idle');
        } catch {
          // Resend failed (e.g. throttled). Keep the modal open; the user can
          // retry once the cooldown elapses.
          setMfaStatus('idle');
          setMfaError('Could not resend the code. Please wait and try again.');
        }
      };

      mfaCancelRef.current = () => {
        closeModal();
        reject(new Error('Sign-in canceled.'));
      };
    });
  };

  const handleStaySignedIn = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      const refreshToken = currentUser?.signInUserSession.refreshToken;
      currentUser.refreshSession(refreshToken, (err?: any, session?: any) => {
        if (session) {
          setShowSessionTimeOutModal(false);
          localStorage.removeItem('session_timeout_modal');
        }
      });
    } catch (error) {}
  };

  useEffect(() => {
    let storageValue = localStorage.getItem('session_timeout_modal');
    if (showSessionTimeOutModal && !storageValue) {
      localStorage.setItem('session_timeout_modal', 'true');
    } else if (storageValue && !showSessionTimeOutModal) {
      localStorage.removeItem('session_timeout_modal');
    }
  }, [showSessionTimeOutModal]);

  // Public, directly-linkable privacy page (not a popup). Rendered here — before the auth gate and
  // without depending on the Router (which lives inside the authed tree) — so /privacy resolves
  // with no session, as required for store/extension review links.
  if (window.location.pathname === '/privacy') {
    return <PrivacyPolicyPage />;
  }
  if (window.location.pathname === '/terms') {
    return (
      <Suspense fallback={null}>
        <TermsOfUsePage />
      </Suspense>
    );
  }

  return (
    <>
      <Global styles={signInCss} />
      {showSessionTimeOutModal && (
        <RefreshSessionModal
          timeoutTime={TIME_REFRESH_TOKEN_MSG}
          setModalMode={setShowSessionTimeOutModal}
          onRefreshSession={handleStaySignedIn}
          onSignOut={handleSignOut}
        />
      )}
      {/*
        While the email-code challenge is active we HIDE the Authenticator
        (keeping it mounted so the in-flight handleSignIn promise and the
        pending Cognito user survive) and render MfaInlinePanel in its place.
        display:'contents' keeps the Authenticator's own layout intact when
        visible; 'none' removes the email/password form from view + tab order.
      */}
      <div style={{ display: mfaOpen ? 'none' : 'contents' }}>
        <Authenticator
          formFields={{
            signIn: {
              username: { label: 'Email', placeholder: 'Enter your email' },
            },
            resetPassword: {
              username: {
                label: 'Enter your email address',
                placeholder: 'Enter your email',
              },
            },
          }}
          hideSignUp={true}
          components={{
            Header: () => components.Header(resetPasswordEmail),
            Footer: () => (
              <View
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingBottom: '20px',
                }}
              >
                <a
                  href="/privacy"
                  style={{
                    ...(fonts.largeLabel as CSSProperties),
                    color: colors.blue3,
                    textDecoration: 'underline',
                  }}
                >
                  Privacy Policy
                </a>
                <a
                  href="/terms"
                  style={{
                    ...(fonts.largeLabel as CSSProperties),
                    color: colors.blue3,
                    textDecoration: 'underline',
                    marginLeft: '24px',
                  }}
                >
                  Terms of Use
                </a>
              </View>
            ),
            ForceNewPassword: components.ForceNewPassword,
            ConfirmResetPassword: {
              // The password rules, above the code/password fields. This
              // Amplify version has no FormFields slot for this route, so the
              // Header slot carries them (its default heading is hidden by
              // signInCss anyway; the app-level Header names the screen).
              Header: LivePasswordRules,
              // "Back to Sign In" + the never-activated-invite hint. See the
              // component for why the hint is shown to everyone.
              Footer: ConfirmResetPasswordFooter,
            },
          }}
          services={{
            handleForgotPassword,
            handleForgotPasswordSubmit,
            handleSignIn,
          }}
        >
          <App />
        </Authenticator>
      </div>
      {mfaOpen && (
        <MfaInlinePanel
          email={mfaEmail}
          status={mfaStatus}
          errorText={mfaError}
          onSubmit={handleMfaSubmit}
          onResend={handleMfaResend}
          onCancel={handleMfaCancel}
        />
      )}
    </>
  );
}

export default function AppWithAuth() {
  return (
    <Authenticator.Provider>
      <AppWithLogin />
    </Authenticator.Provider>
  );
}
